// hooks/use-language.tsx
import { useCallback, useEffect } from "react";
import { useParams } from "react-router-dom";

// Store
import { detectLanguage, setLanguage } from "store/slices/language-slice";
import { useAppDispatch, useAppSelector } from "store/store";
import type { RootState } from "store/store";

// Language configuration
import {
  DEFAULT_LANGUAGE,
  getLanguageFromUrlSegment,
  getUrlSegmentForLanguage,
  isValidUrlSegment,
} from "config/language-config";

// ============================================================================
// Types
// ============================================================================

interface UseLanguageOptions {
  /** Whether to auto-detect language on mount. Defaults to true */
  autoDetect?: boolean;
  /** Whether to sync language from URL path on mount. Defaults to false */
  syncFromUrl?: boolean;
}

interface UseLanguageReturn {
  /** Current language code from the store */
  currentLanguage: string;
  /** Active language code (from URL param or store) */
  activeLang: string;
  /** Whether language detection is in progress */
  isLoading: boolean;
  /** Source of detected language */
  detectionSource: "localStorage" | "browser" | "geo" | "default" | null;
  /** Check if a URL segment is a valid language */
  isValidLanguageSegment: (segment: string) => boolean;
  /** Get language code from URL segment (returns null if invalid) */
  getLanguageFromSegment: (segment: string) => string | null;
  /** Extract and get language from the current URL path */
  getLanguageFromUrl: () => string | null;
  /** Set the language in the store */
  updateLanguage: (language: string) => void;
  /** Sync language from URL if valid, otherwise return the current language */
  syncLanguageFromUrl: () => string;
  /** Generate a path with the active language prefix */
  getLanguagePath: (path: string) => string;
  /** Get URL segment for current active language */
  getUrlSegment: () => string;
}

// ============================================================================
// Hook
// ============================================================================

export default function useLanguage(
  options: UseLanguageOptions = {}
): UseLanguageReturn {
  const { autoDetect = true, syncFromUrl = false } = options;

  const dispatch = useAppDispatch();

  // Get language state from store
  const {
    current: currentLanguage,
    isLoading,
    detectionSource,
  } = useAppSelector((state: RootState) => state.language);

  // Get lang from URL params (for react-router-dom)
  const { lang: urlLangParam } = useParams<{ lang?: string }>();

  // Resolve active language: URL param > Redux store > default
  const activeLang = (() => {
    if (urlLangParam) {
      const resolved = getLanguageFromUrlSegment(urlLangParam);
      if (resolved) return resolved;
    }
    return currentLanguage || DEFAULT_LANGUAGE;
  })();

  /**
   * Check if a URL segment is a valid language
   */
  const isValidLanguageSegment = useCallback((segment: string): boolean => {
    return isValidUrlSegment(segment);
  }, []);

  /**
   * Get language code from a URL segment
   */
  const getLanguageFromSegment = useCallback(
    (segment: string): string | null => {
      return getLanguageFromUrlSegment(segment);
    },
    []
  );

  /**
   * Extract the first path segment from URL and get language code
   */
  const getLanguageFromUrl = useCallback((): string | null => {
    const segments = globalThis.location.pathname.split("/").filter(Boolean);
    const firstSegment = segments[0];
    if (!firstSegment) return null;
    return getLanguageFromUrlSegment(firstSegment);
  }, []);

  /**
   * Update the language in the store
   */
  const updateLanguage = useCallback(
    (language: string) => {
      dispatch(setLanguage(language));
    },
    [dispatch]
  );

  /**
   * Sync language from URL path if valid, otherwise return current language.
   * Also dispatches the language update if URL language is valid.
   */
  const syncLanguageFromUrl = useCallback((): string => {
    const urlLanguage = getLanguageFromUrl();

    if (urlLanguage) {
      dispatch(setLanguage(urlLanguage));
      return urlLanguage;
    }

    return currentLanguage || DEFAULT_LANGUAGE;
  }, [currentLanguage, dispatch, getLanguageFromUrl]);

  /**
   * Get URL segment for current active language
   */
  const getUrlSegment = useCallback((): string => {
    return getUrlSegmentForLanguage(activeLang);
  }, [activeLang]);

  /**
   * Generate a path with the active language prefix
   */
  const getLanguagePath = useCallback(
    (path: string): string => {
      const urlSegment = getUrlSegmentForLanguage(activeLang);

      // Handle root path
      if (path === "/") {
        return `/${urlSegment}`;
      }

      // Ensure path starts with /
      const cleanPath = path.startsWith("/") ? path : `/${path}`;
      return `/${urlSegment}${cleanPath}`;
    },
    [activeLang]
  );

  // Auto-detect language on mount
  useEffect(() => {
    if (autoDetect) {
      dispatch(detectLanguage());
    }
  }, [autoDetect, dispatch]);

  // Sync from URL on mount if enabled
  useEffect(() => {
    if (syncFromUrl) {
      syncLanguageFromUrl();
    }
  }, [syncFromUrl, syncLanguageFromUrl]);

  return {
    currentLanguage,
    activeLang,
    isLoading,
    detectionSource,
    isValidLanguageSegment,
    getLanguageFromSegment,
    getLanguageFromUrl,
    updateLanguage,
    syncLanguageFromUrl,
    getLanguagePath,
    getUrlSegment,
  };
}
